import type { Country, Currency, TripType as TripTypeValues } from '@eti/schema-types';
import { TripType } from '@eti/schema-types';
import { addHours, isBefore } from 'date-fns';
import { ONLINE_CHECK_IN } from '@eti/products';
import { decorateUrlWithLangOverrideParam } from '../../utils/ibeRequest';
import type {
  AddOnOrdersType,
  AncillaryBundlesType,
  AvailableExtraProductsType,
  AvailableExtraProductType,
  BoundsType,
  BoundType,
  CustomerDetailsType,
  ExtraProductsType,
  FlightInformationType,
  PurchasedCheckOutType,
  RefundStatusType,
  RegionType,
  SegmentsType,
  TripSegmentCarrierBookingNumberType,
  TripSegmentType,
} from './models';
import { PurchasedCheckInType } from './models';
import { hasRefundProviderBookingToShow } from './components/refund-status/utils';

export type GroupedAirlineReferenceInfoType = {
  arrivedAt: string;
  carrierBookingNumber: TripSegmentCarrierBookingNumberType;
  departuredAt: string;
  destination: RegionType;
  emailInfo: { email: string; isAlias: boolean };
  origin: RegionType;
};

export const getJourneyType = (type: TripType) => {
  if (
    (
      [
        TripType.OpenJawSingleOrigin,
        TripType.OpenJawSingleDestination,
        TripType.OpenJawDouble,
      ] as TripTypeValues[]
    ).includes(type)
  ) {
    return TripType.MultiStop;
  }
  return type;
};

export const getTripSegments = (bound: BoundType) =>
  bound.segments.filter(
    (segment): segment is TripSegmentType => segment.__typename === 'TripSegment',
  );

export const getTripSegmentsFromBounds = (bounds: BoundsType): TripSegmentType[] => {
  return bounds.flatMap((bound) => getTripSegments(bound));
};

export const transformFlightInformationToRecord = (flightInformation: FlightInformationType) =>
  flightInformation.reduce<Record<string, FlightInformationType[number]>>((acc, element) => {
    if (!acc[element.pnr]) {
      acc[element.pnr] = element;
    }

    return acc;
  }, {});

const getSegmentsGroupedByBookingNumberAndCarrierCode = (
  segments: SegmentsType,
  flightInfoRecord: Record<string, FlightInformationType[number]>,
  customerDetails: CustomerDetailsType,
): Record<string, GroupedAirlineReferenceInfoType> =>
  segments.reduce((acc: Record<string, GroupedAirlineReferenceInfoType>, segment) => {
    if (segment?.__typename !== 'TripSegment') {
      return acc;
    }

    if (segment.pnr && customerDetails?.email) {
      const key =
        (segment.carrierBookingNumber?.carrierCode || segment.operatingCarrier.code) +
        (segment.carrierBookingNumber?.bookingNumber || '_not_available');
      const pnrFlightInfo = flightInfoRecord[segment.pnr];
      const shouldUseAliasEmail =
        !pnrFlightInfo.externalFlightVerificationInfo.isExternalFlightVerified &&
        Boolean(pnrFlightInfo.loginEmail);
      const email = shouldUseAliasEmail ? pnrFlightInfo.loginEmail!! : customerDetails.email;

      if (acc[key]) {
        acc[key].destination = segment.destination;
        acc[key].arrivedAt = segment.arrivedAt;
      } else {
        acc[key] = {
          arrivedAt: segment.arrivedAt,
          carrierBookingNumber: segment.carrierBookingNumber || {
            carrierName: segment.operatingCarrier.name,
            carrierCode: segment.operatingCarrier.code,
            bookingNumber: null,
          },
          departuredAt: segment.departuredAt,
          destination: segment.destination,
          emailInfo: { email, isAlias: shouldUseAliasEmail },
          origin: segment.origin,
        };
      }
    }
    return acc;
  }, {});

export const getGroupedAirlineReferenceInfo = (
  bounds: BoundsType,
  customerDetails: CustomerDetailsType,
  flightInformation: FlightInformationType,
): GroupedAirlineReferenceInfoType[] => {
  const flightInfoRecord = transformFlightInformationToRecord(flightInformation);

  return bounds.flatMap((bound) => {
    return Object.values(
      getSegmentsGroupedByBookingNumberAndCarrierCode(
        bound.segments,
        flightInfoRecord,
        customerDetails,
      ),
    );
  });
};

const getBoundsInfoString = (bounds: BoundsType): string => {
  return bounds
    .map((bound) => {
      const firstSegment = bound.segments[0];
      const lastSegment = bound.segments[bound.segments.length - 1];
      if (firstSegment?.__typename !== 'TripSegment' || lastSegment?.__typename !== 'TripSegment') {
        return '';
      }
      return `${firstSegment.origin.code},${lastSegment.destination.code}`;
    })
    .join(',');
};

export const createGoClimateUrl = (
  bounds: BoundsType,
  goClimateLink: string,
  goClimateMarketCodes: Array<string>,
  market: Country,
): string => {
  const { code } = market;
  const marketCode = goClimateMarketCodes?.includes(code) ? `/${code.toLowerCase()}` : '';
  const segments = getTripSegmentsFromBounds(bounds);
  const cabinCass = segments[0]?.cabinClassName?.toLowerCase() || '';
  const boundsInfo = getBoundsInfoString(bounds);

  return `${goClimateLink}${marketCode}/flight_offsets/new?offset_params=${cabinCass},${boundsInfo}`;
};

export type OriginDestinationPerBoundType = {
  origin: string;
  destination: string;
}[];

export const getOriginDestinationPerBound = (bounds: BoundsType): OriginDestinationPerBoundType =>
  bounds.reduce((acc: { origin: string; destination: string }[], bound) => {
    const firstSegment = bound.segments[0];
    const lastSegment = bound.segments[bound.segments.length - 1];
    if (firstSegment?.__typename === 'TripSegment' && lastSegment?.__typename === 'TripSegment') {
      acc.push({
        origin: firstSegment.origin.cityName,
        destination: lastSegment.destination.cityName,
      });
    }
    return acc;
  }, []);

export const formatAmountInChargingCurrency = (
  locale: string,
  amount: number,
  currency: Currency,
) => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.code,
  }).format(amount / 10 ** currency.exponent);
};

export const priceWithFullNameCurrency = (price: string, locale: string, currency: Currency) => {
  const currencyFullName = new Intl.DisplayNames([locale], { type: 'currency' }).of(currency.code);
  const priceWithoutCurrencySymbol = price.replace(/[^\d.,]/g, '');
  return `${priceWithoutCurrencySymbol} ${currencyFullName}`;
};

export const shouldShowRefundStatusInformation = (refundStatus: RefundStatusType) => {
  if (!refundStatus) {
    return false;
  }

  if (refundStatus.hasChargeback) {
    return true;
  }

  if (refundStatus.cases.length === 0) {
    return false;
  }

  return refundStatus.cases.some((refundCase) =>
    refundCase.providerBookings.some((providerBooking) =>
      hasRefundProviderBookingToShow(refundCase.cancelReason, providerBooking),
    ),
  );
};

export const hasRyanairMarketingCarrierAndSupplier = (
  ryanairMarketingCarriers: string[],
  lccSuppliersForVerification: string[],
  tripSegments: TripSegmentType[],
) => {
  const lowerCaseRyanairMarketingCarriers = ryanairMarketingCarriers.map((carrier) =>
    carrier.toLowerCase(),
  );
  const lowerCaseLccSuppliersForVerification = lccSuppliersForVerification.map((supplier) =>
    supplier.toLowerCase(),
  );

  return tripSegments.some(
    (segment) =>
      segment.marketingCarrier &&
      segment.marketingCarrier.code &&
      segment.lccSupplier &&
      lowerCaseRyanairMarketingCarriers.includes(segment.marketingCarrier.code.toLowerCase()) &&
      lowerCaseLccSuppliersForVerification.includes(segment.lccSupplier.toLowerCase()),
  );
};

export const isUpcoming = (bounds: BoundsType) => {
  const lastBound = bounds[bounds.length - 1];
  const lastArrivalDate = new Date(
    (lastBound?.segments[lastBound?.segments.length - 1] as TripSegmentType)?.arrivedAt,
  );
  const currentDate = addHours(new Date(), 2);
  return isBefore(currentDate, lastArrivalDate);
};

export const getTravelDocUrl = (travelDocUrl: string, isRTL: boolean) => {
  const ENGLISH_LANGUAGE_ID = 1;
  return isRTL ? decorateUrlWithLangOverrideParam(travelDocUrl, ENGLISH_LANGUAGE_ID) : travelDocUrl;
};

export const getRyanairLocale = (flightInformation: FlightInformationType) => {
  const flightInfoWithLocale = flightInformation.find(
    (fi) => fi.externalFlightVerificationInfo.locale !== null,
  );
  const locale = flightInfoWithLocale?.externalFlightVerificationInfo.locale || null;
  return locale ? `${locale.countryCode}/${locale?.languageCode}` : 'gb/en';
};

// TODO: delete together with check-in v1, when v2 is enabled (its tests too)
export const getPurchasedCheckInType = (
  extraProducts: ExtraProductsType,
  addOnOrders: AddOnOrdersType,
  ancillaryBundles: AncillaryBundlesType,
): PurchasedCheckOutType | null => {
  if (extraProducts) {
    const checkInProductFromPrimaryOrder = extraProducts.find(
      (product) => Number(product?.id) === ONLINE_CHECK_IN.id,
    );

    if (checkInProductFromPrimaryOrder) {
      return checkInProductFromPrimaryOrder?.price?.price?.value === 0
        ? PurchasedCheckInType.App
        : PurchasedCheckInType.Service;
    }
  }

  if (addOnOrders) {
    for (const addOnOrder of addOnOrders) {
      const checkInProductFromAddOnOrder = addOnOrder?.extraProducts?.find(
        (product) => Number(product?.id) === ONLINE_CHECK_IN.id,
      );

      if (checkInProductFromAddOnOrder) {
        return checkInProductFromAddOnOrder?.price?.price?.value === 0
          ? PurchasedCheckInType.App
          : PurchasedCheckInType.Service;
      }
    }
  }

  const checkInProductFromAncillaryBundle = ancillaryBundles?.find((ancillaryBundle) => {
    if (!ancillaryBundle?.selectedOption) {
      return null;
    }
    const selectedBundle = ancillaryBundle?.options?.find(
      (option) => option?.id && option.id === ancillaryBundle.selectedOption,
    );
    return selectedBundle?.content?.find(
      (product) => Number(product?.productTypeId) === ONLINE_CHECK_IN.id,
    );
  });

  if (checkInProductFromAncillaryBundle) {
    return PurchasedCheckInType.Service;
  }
  return null;
};

export const hasCheckInProduct = (
  extraProducts: ExtraProductsType,
  addOnOrders: AddOnOrdersType,
  ancillaryBundles: AncillaryBundlesType,
): boolean => {
  return (
    extraProducts.some((product) => Number(product.id) === ONLINE_CHECK_IN.id) ||
    addOnOrders.some(
      (order) => order.extraProducts?.some((product) => Number(product?.id) === ONLINE_CHECK_IN.id),
    ) ||
    ancillaryBundles.some(
      (bundle) =>
        bundle.selectedOption &&
        bundle.options.some(
          (option) =>
            option?.id === bundle.selectedOption &&
            option?.content?.some(
              (product) => Number(product?.productTypeId) === ONLINE_CHECK_IN.id,
            ),
        ),
    ) ||
    false
  );
};

export const isCheckInAvailableToBuy = (availableExtraProducts?: AvailableExtraProductsType) =>
  availableExtraProducts?.some(
    (product: AvailableExtraProductType) => Number(product?.id) === Number(ONLINE_CHECK_IN.id),
  );
